import { Vue, Component, Prop } from 'vue-property-decorator'
import { ConfigurationDeviceResponse } from '@/modules/configurationModule/detail/instrumentation/devices/services/configuration-device/configuration-device.dto'
import {
    ConfigurationInstrumentationDevicesModalNotificationsFormState,
    initialConfigurationInstrumentationDevicesModalNotificationsFormState
} from '@/modules/configurationModule/detail/instrumentation/devices/components/modal-notifications-form/configuration-instrumentation-devices-modal-notifications-form.model'
import {
    ConfigurationDefaultDeviceConfigurationCreateRequest,
    ConfigurationDefaultDeviceConfigurationResponse
} from '@/modules/configurationModule/detail/instrumentation/devices/services/configuration-default-device-configuration/configuration-default-device-configuration.dto'
import { configurationDefaultDeviceConfigurationService } from '@/modules/configurationModule/detail/instrumentation/devices/services/configuration-default-device-configuration/configuration-default-device-configuration.service'
import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { OperationResponse } from '@/modules/configurationModule/detail/local-laboratory-module/sections/services/operation/operation.dto'
import { operationService } from '@/modules/configurationModule/detail/local-laboratory-module/sections/services/operation/operation.service'
import { ConfigurationSectionResponse } from '@/modules/configurationModule/detail/local-laboratory-module/sections/services/configuration-section/configuration-section.dto'
import { configurationSectionService } from '@/modules/configurationModule/detail/local-laboratory-module/sections/services/configuration-section/configuration-section.service'
import { AssayModalAssayWithValuableConceptResponse } from '@/components/assays-view/services/assays-view-assay/assays-view-assay.dto'
import { assaysViewAssayService } from '@/components/assays-view/services/assays-view-assay/assays-view-assay.service'
import { GridUtils } from '@/shared/util/grid-utils'

@Component
export default class ConfigurationInstrumentationDevicesModalNotificationsForm extends Vue {
    @Prop(String) readonly actionTitle!: string
    @Prop() readonly device!: ConfigurationDeviceResponse

    state: ConfigurationInstrumentationDevicesModalNotificationsFormState

    get canAdd(): boolean {
        return !!this.state.operation?.id
    }

    constructor() {
        super()
        this.state = initialConfigurationInstrumentationDevicesModalNotificationsFormState()
    }

    /// SERVICES CALLS ///

    private async _getOperations(): Promise<OperationResponse[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await this.$_toCallSecureLoading(operationService.paginator(undefined, params))
        return response?.content ?? []
    }

    private async _getSections(): Promise<ConfigurationSectionResponse[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await this.$_toCallSecureLoading(configurationSectionService.paginator(undefined, params, this.state.laboratoryId))
        return response?.content ?? []
    }

    private async _getAssays(): Promise<AssayModalAssayWithValuableConceptResponse[]> {
        const response = await this.$_toCallSecureLoading(assaysViewAssayService.getCompletedAssays(this.state.laboratoryId))
        return response ?? []
    }

    private async _createDefaultDeviceConfiguration(createRequest: ConfigurationDefaultDeviceConfigurationCreateRequest): Promise<ConfigurationDefaultDeviceConfigurationResponse | null> {
        const response = await this.$_toCallUnique(configurationDefaultDeviceConfigurationService.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _deleteDefaultDeviceConfiguration(defaultDeviceConfigurationId: number): Promise<void | null> {
        const response = await this.$_toCallUnique(configurationDefaultDeviceConfigurationService.delete(this.state.laboratoryId, defaultDeviceConfigurationId))
        if (response !== null) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted'))
            return response
        }
        return null
    }

    private async _getDefaultDeviceConfigurations(): Promise<ConfigurationDefaultDeviceConfigurationResponse[]> {
        const filterModel: CommonFilterModel = {
            'device.id': GridUtils.getNumberFilterModel('equals', this.device.id)
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await this.$_toCallSecureLoading(configurationDefaultDeviceConfigurationService.paginator(filterModel, params, this.state.laboratoryId))
        return response?.content ?? []
    }

    ///

    private async _loadOperations(): Promise<void> {
        this.state.operations = await this._getOperations()
    }

    private async _loadSections(): Promise<void> {
        const sections = await this._getSections()
        this.state.sections = sections.filter(section => section.active)
    }

    private async _loadAssays(): Promise<void> {
        const assays = await this._getAssays()
        this.state.assays = assays.filter(assay => assay.active)
    }

    private async _loadDefaultDeviceConfigurations(): Promise<void> {
        this.state.defaultDeviceConfigurations = await this._getDefaultDeviceConfigurations()
    }

    /// HOOKS ///

    async mounted(): Promise<void> {
        try {
            this.$_updateLoading(true)

            await Promise.all([
                this._loadOperations(),
                this._loadSections(),
                this._loadAssays(),
                this._loadDefaultDeviceConfigurations()
            ])
        } finally {
            this.$_updateLoading(false)
        }
    }

    ///

    getSectionLabelFromDefaultDeviceConfiguration(defaultDeviceConfiguration: ConfigurationDefaultDeviceConfigurationResponse): string {
        return defaultDeviceConfiguration.section?.name ?? ''
    }

    getAssayLabelFromDefaultDeviceConfiguration(defaultDeviceConfiguration: ConfigurationDefaultDeviceConfigurationResponse): string {
        return defaultDeviceConfiguration.assay?.name ?? ''
    }

    /// OPERATIONS ///

    async addOperation(): Promise<void> {
        const request: ConfigurationDefaultDeviceConfigurationCreateRequest = {
            deviceId: this.device.id,
            operationId: this.state.operation!.id,
            assayId: this.state.assay?.id,
            sectionId: this.state.section?.id
        }
        if (await this._createDefaultDeviceConfiguration(request)) {
            await this._loadDefaultDeviceConfigurations()
            this.state = {
                ...this.state,
                operation: null,
                section: null,
                assay: null
            }
        }
    }

    async deleteOperation(defaultDeviceConfigurationId: number): Promise<void> {
        if (await this._deleteDefaultDeviceConfiguration(defaultDeviceConfigurationId) !== null) {
            await this._loadDefaultDeviceConfigurations()
        }
    }
}
